import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
  FormControlLabel,
  TextField,
  Switch,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { SelectComponent } from 'src/views/Donations/EditRecurringDonations';
import useVehicles from 'src/hooks/useVehicles';
import useAuth from 'src/hooks/useAuth';
import useOrgsForFoodRunCreation from 'src/hooks/useOrgsForFoodRunCreation';
import { useTranslation } from 'react-i18next';
import DriverSelector from 'src/components/DriverSelector';
import useDriverAvailability from 'src/hooks/useDriverAvailability';

const DriverSelectorModal = ({
  open,
  onClose,
  handleDriverAssign,
  donation,
  canConvertToFoodRun = false,
  setCurrentUserAsDefault = false,
  isFoodRun = false,
}) => {
  const { t } = useTranslation('translation');
  const { user, isFoodMover } = useAuth();
  const { recipients, isMyRecipientsEmpty, fetchNearByRecipients } =
    useOrgsForFoodRunCreation({
      lat: user.lat,
      lng: user.lng,
      fetchOnMount: true,
    });

  const { vehicles = [] } = useVehicles();
  const { checkAvailabilityForDrivers, drivers } = useDriverAvailability();
  const [showConvertToFoodRun, setShowConvertToFoodRun] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [dropoffInstructions, setDropoffInstructions] = useState('');
  const [convertSingleDonation, setConvertSingleDonation] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const donationRescueDrivers = useMemo(
    () =>
      Array.isArray(donation?.rescue_drivers) ? donation.rescue_drivers : [],
    [donation?.rescue_drivers]
  );

  const handleAssignClick = async () => {
    if (isSubmitting) return;

    if (selectedDrivers.length === 0) {
      enqueueSnackbar(t('donations.select-a-driver'), { variant: 'error' });
      return;
    }
    if (showConvertToFoodRun && !selectedRecipient) {
      enqueueSnackbar(t('donations.select-a-receiver'), {
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await handleDriverAssign({
        showConvertToFoodRun: showConvertToFoodRun ? selectedRecipient : null,
        pickupInstructions,
        dropoffInstructions,
        convertSingleDonation,
        drivers: selectedDrivers,
        recipientTeamId: selectedRecipient,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedDrivers(donationRescueDrivers);
    }

    if (donation?.start_date && donation?.end_date) {
      checkAvailabilityForDrivers({
        pickupTime: donation.start_date,
        endTime: donation.end_date,
      });
    }
  }, [
    checkAvailabilityForDrivers,
    donation?.end_date,
    donation?.start_date,
    donationRescueDrivers,
    open,
  ]);

  useEffect(() => {
    if (setCurrentUserAsDefault) {
      let currentUserFoundInDriver = drivers.find(
        (member) => member.id === user.id
      );
      if (currentUserFoundInDriver?.id && !selectedDriverId) {
        setSelectedDriverId(currentUserFoundInDriver.id);
      }
    }
  }, [donation, drivers.length, setCurrentUserAsDefault]);

  useEffect(() => {
    if (canConvertToFoodRun && isMyRecipientsEmpty) {
      fetchNearByRecipients({
        donorLat: JSON.parse(donation?.donor_location || '{}').lat,
        donorLng: JSON.parse(donation?.donor_location || '{}').lng,
      });
    }
  }, [canConvertToFoodRun, isMyRecipientsEmpty, donation]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '800px',
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body">
            {isFoodRun
              ? t('donations.assign-a-driver-for-this-food-run')
              : t('donations.assign-a-driver-for-this-rescue')}
          </Typography>
          <IconButton onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          pb: 0,
        }}
      >
        <Box>
          {!donation?.is_partial &&
          canConvertToFoodRun &&
          isFoodMover &&
          user?.is_non_profit &&
          !user.is_driver ? (
            <FormControlLabel
              sx={{
                mb: 1,
              }}
              label={t('donations.convert-to-food-run')}
              control={
                <Switch
                  checked={showConvertToFoodRun}
                  onChange={() => setShowConvertToFoodRun((prev) => !prev)}
                />
              }
            />
          ) : null}
          <DriverSelector
            sx={{
              mt: 1,
            }}
            drivers={drivers}
            driversValue={selectedDrivers}
            onChange={(drivers) => {
              setSelectedDrivers(drivers);
            }}
            vehicles={vehicles}
          />
        </Box>

        <Collapse in={showConvertToFoodRun}>
          {donation?.is_recurring ? (
            <Box>
              <RadioGroup
                value={convertSingleDonation == true}
                onChange={(e) =>
                  setConvertSingleDonation(e.target.value == 'true')
                }
                name="convert-single-donation"
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label={t(
                    'donations.convert-all-future-donations-to-food-runs'
                  )}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label={t('donations.convert-only-this-donation')}
                />
              </RadioGroup>
            </Box>
          ) : null}
          <Box pt={2}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
              }}
            >
              {t('donations.assign-recipient')}
            </Typography>
            <SelectComponent
              label={t('donations.recipient-name')}
              value={selectedRecipient}
              onChange={(e) => setSelectedRecipient(e.target.value)}
              items={recipients.map((receipient) => ({
                value: receipient.team_id,
                title: (
                  <>
                    {receipient.name} &nbsp;
                    <Typography color="text.secondary" variant="overline">
                      {receipient.formatted_address}
                    </Typography>
                  </>
                ),
              }))}
              error={null}
              helperText={null}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            py={2}
            spacing={2}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                {t('donations.pickup-instructions')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                inputProps={{ maxLength: 1000 }}
                helperText={`${pickupInstructions.length} / 1000`}
                value={pickupInstructions}
                onChange={(e) => setPickupInstructions(e.target.value)}
                label={t('donations.pickup-instructions', {
                  context: 'optional',
                })}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                {t('donations.dropoff-instructions')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                inputProps={{ maxLength: 1000 }}
                helperText={`${dropoffInstructions.length} / 1000`}
                value={dropoffInstructions}
                onChange={(e) => setDropoffInstructions(e.target.value)}
                label={t('donations.dropoff-instructions', {
                  context: 'optional',
                })}
              />
            </Box>
          </Stack>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button
          data-tour="donation-assign-driver-button"
          color="secondary"
          variant="contained"
          fullWidth
          onClick={handleAssignClick}
          disabled={isSubmitting}
          size="large"
        >
          {t('donations.assign-drivers')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverSelectorModal;
